import { useMemo, useCallback } from 'react';
import { useAppSelector } from 'app/store';
import { selectAgaveSlice } from 'app/pages/store/agaveSlice';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { defaultIdentifierOrder, formatWithIdentifier, useGetOptionLabel } from 'app/hooks/useGetOptionLabel';
import { useAccountType } from 'app/hooks/useAccountType';
import { isCodingSortedByCode, supportsCodeSort } from 'util/codingSortSetting';
import type {
  FlexGlCode,
  FlexCode,
  FlexCostCode,
  FlexCostType,
  FlexVendor,
  AgaveGlCode,
  AgaveProject,
  AgaveCostCode,
  AgaveCostType,
  AgaveSubJob,
  AgaveVendor,
  AgaveLocation,
  AgaveDepartment,
  AgaveSubaccount,
  AgaveEmployeeEntity,
  AgaveEquipment,
  AgaveEmCostCode,
  AgaveEmCostType,
} from 'API';
import type { ErpPostingAccount } from 'app/services/erp-connection.service';

type CodingType =
  | FlexGlCode
  | FlexCode
  | FlexCostCode
  | FlexCostType
  | FlexVendor
  | AgaveGlCode
  | AgaveProject
  | AgaveCostCode
  | AgaveCostType
  | AgaveVendor
  | AgaveSubJob
  | AgaveLocation
  | AgaveDepartment
  | AgaveSubaccount
  | AgaveEmployeeEntity
  | AgaveEquipment
  | AgaveEmCostCode
  | AgaveEmCostType
  | ErpPostingAccount;

/**
 * Custom hook to handle display formatting for both Flex and Agave coding fields.
 * Provides consistent formatting across mobile and desktop views.
 *
 * @returns Object containing:
 * - getDisplayName: Function to format any coding type (Flex or Agave)
 * - useAgave: Boolean indicating if Agave ERP is active
 * - useFlex: Boolean indicating if Flex ERP is active
 * - isQBDClient: Boolean indicating QuickBooks Desktop
 * - isQBOClient: Boolean indicating QuickBooks Online
 */
export const useCodingDisplay = () => {
  const { agaveAccount } = useAppSelector(selectAgaveSlice);
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  const { hasErpCodingUI, isConstructionType, isFlexCodingEnabled } = useAccountType();

  const useAgave = hasErpCodingUI && isConstructionType;
  const useFlex = isFlexCodingEnabled;

  const includeDescriptionForCostCodes = useMemo(
    () => selectedPaidol?.erpCostCodesUseDescriptionSetting?.isEnabled ?? false,
    [selectedPaidol?.erpCostCodesUseDescriptionSetting?.isEnabled]
  );

  // SEE-1325: the org's dropdown-sort choice also picks the label format, so the
  // list reads in the order it is sorted — "(41.1.135) - Sch 40 Pipe" when sorted
  // by code, "Sch 40 Pipe - (41.1.135)" when sorted by name. Defaults to the
  // existing 'after' format for every paidol that has not opted in.
  //
  // Applied ONLY to the label getters for entities the toggle reorders (the
  // CODING_SORT_FIELDS set: GL codes, projects, cost codes, cost types,
  // vendors, the EM lists, and since SEE-1622 sub-jobs, departments,
  // sub-accounts and employees). Locations have no entry — they have no code and
  // are name-ordered in both states, so their labels must stay put too, or the
  // toggle flips a format on lists it never reorders.
  const identifierOrder = useMemo<'before' | 'after'>(
    // The account slug is the fallback for paidols that predate the
    // agaveSourceSystemSlug backfill — without it they'd see the toggle
    // (the visibility gate falls back) but no label would ever flip.
    () =>
      isCodingSortedByCode(selectedPaidol, agaveAccount?.source_system?.slug)
        ? 'before'
        : defaultIdentifierOrder,
    [selectedPaidol, agaveAccount?.source_system?.slug]
  );

  // SEE-1030: after an Agave->direct swap the live `agaveAccount` is gone, so
  // fall back to the paidol's persisted source-system slug so a migrated QBO
  // paidol still renders coding labels the QBO way (name only, no "- (number)"
  // suffix where the number is just the source_id). Non-QBO ERPs are unaffected.
  const sourceSystemSlug =
    agaveAccount?.source_system?.slug ?? selectedPaidol?.agaveSourceSystemSlug ?? undefined;

  const isQBDClient = useMemo(() => sourceSystemSlug === 'quick-books-desktop', [sourceSystemSlug]);

  const isQBOClient = useMemo(() => sourceSystemSlug === 'quick-books-online', [sourceSystemSlug]);

  // SEE-1325 / SEE-1622: direct ERPs with meaningful codes label vendors with
  // their vendor code rather than repeating the name. Must match
  // AgaveVendorsDropdown's gate: the dropdown
  // list and the selected value are labelled by different code paths, so gating
  // only one leaves the list reading "Speedchain - (2)" and the coded cell
  // reverting to "Speedchain - (Speedchain)" -- which defeats the point, since
  // three vendors share that name and only the number says which AP company.
  const showErpCode = useMemo(
    // The shared predicate, not an inline copy — the same one the dropdown and
    // the filter panel use, so all three label vendors alike.
    () => supportsCodeSort(selectedPaidol, agaveAccount?.source_system?.slug),
    [selectedPaidol, agaveAccount?.source_system?.slug]
  );

  const getAgaveGlLabel = useGetOptionLabel({
    isQBDClient,
    isQBOClient,
    identifierField: 'number',
    identifierOrder,
    nameField: 'name',
  });

  const getAgaveProjectLabel = useGetOptionLabel({
    isQBDClient,
    isQBOClient,
    identifierField: 'number',
    identifierOrder,
    nameField: 'name',
  });

  const getAgaveCostCodeLabel = useGetOptionLabel({
    isQBDClient,
    isQBOClient,
    identifierField: 'code',
    identifierOrder,
    nameField: 'name',
    includeDescription: includeDescriptionForCostCodes,
  });

  const getAgaveCostTypeLabel = useGetOptionLabel({
    isQBDClient,
    isQBOClient,
    identifierField: 'code',
    identifierOrder,
    nameField: 'name',
  });

  const getAgaveSubJobLabel = useGetOptionLabel({
    isQBDClient,
    isQBOClient,
    // SEE-1622: `code`, as AgaveSubJobsDropdown uses — sub-job rows populate
    // `code`, never `number` (SEE-1207), so `number` rendered name-only
    // selected values. Sub-jobs joined CODING_SORT_FIELDS, so the toggle
    // reorders them and reformats them too, matching the dropdown's own label.
    identifierField: 'code',
    identifierOrder,
    nameField: 'name',
  });

  const getAgaveVendorLabel = useGetOptionLabel({
    isQBDClient,
    isQBOClient,
    identifierField: showErpCode ? 'code' : 'name',
    identifierOrder,
    nameField: 'name',
  });

  // Locations: not in CODING_SORT_FIELDS — no code, so the toggle never
  // reorders them and must not reformat them either.
  const getAgaveLocationLabel = useGetOptionLabel({
    isQBDClient,
    isQBOClient,
    identifierField: 'name',
    identifierOrder: defaultIdentifierOrder,
  });

  // SEE-1622: departments and sub-accounts carry their ERP code (Acumatica
  // SERVEAST / 000CON, Intacct department ids) like vendors and employees —
  // same gate as their dropdowns, and in CODING_SORT_FIELDS, so the label
  // follows the toggle instead of repeating the name.
  const getAgaveDepartmentLabel = useGetOptionLabel({
    isQBDClient,
    isQBOClient,
    identifierField: showErpCode ? 'code' : 'name',
    identifierOrder,
  });

  const getAgaveSubaccountLabel = useGetOptionLabel({
    isQBDClient,
    isQBOClient,
    identifierField: showErpCode ? 'code' : 'name',
    identifierOrder,
  });

  // SEE-1622: employees carry their ERP employee id like vendors carry their
  // code — same gate as AgaveEmployeesDropdown, and in CODING_SORT_FIELDS, so
  // the label follows the toggle.
  const getAgaveEmployeeLabel = useGetOptionLabel({
    isQBDClient,
    isQBOClient,
    identifierField: showErpCode ? 'source_id' : 'name',
    identifierOrder,
  });

  const getDisplayName = useCallback(
    (item: CodingType | null | undefined): string => {
      if (!item) return '';

      // SEE-1135 — billable item (ErpPostingAccount): "Name - (ITEMID)", matching
      // the desktop dropdown + settings sheet. Checked first since it has neither
      // __typename nor source_id and would otherwise fall through to ''.
      if ('accountId' in item) {
        const { accountId, accountName } = item;
        return accountName && accountName !== accountId ? `${accountName} - (${accountId})` : accountId;
      }

      if ('__typename' in item && item.__typename) {
        // The CDK v2 APIs (e.g. reimbursements) return V2-prefixed typenames
        // (`V2AgaveCostType`), while the main Amplify API returns the unprefixed
        // ones (`AgaveCostType`). Normalize so both dispatch to the same
        // formatter — otherwise V2 objects fall through to the bare-code
        // fallback and render as just their numeric code.
        const typename = item.__typename.replace(/^V2/, '');
        switch (typename) {
          case 'AgaveGlCode':
            return getAgaveGlLabel(item);
          case 'AgaveProject':
            return getAgaveProjectLabel(item);
          case 'AgaveCostCode':
            return getAgaveCostCodeLabel(item);
          case 'AgaveCostType':
            return getAgaveCostTypeLabel(item);
          case 'AgaveSubJob':
            return getAgaveSubJobLabel(item);
          case 'AgaveVendor':
            return getAgaveVendorLabel(item);
          case 'AgaveLocation':
            return getAgaveLocationLabel(item);
          case 'AgaveDepartment':
            return getAgaveDepartmentLabel(item);
          case 'AgaveSubaccount':
            return getAgaveSubaccountLabel(item);
          case 'AgaveEmployeeEntity':
            return getAgaveEmployeeLabel(item);
          // SEE-1091 — Vista equipment coding. SEE-1325: all three follow
          // `identifierOrder` like every other coding type, so the label reads
          // in the order the list is sorted — name-first when off, code-first
          // when on. Blank-named rows label as the bare code (the
          // formatWithIdentifier guard) and sort last (sortCodingOptions).
          // (Switching on the normalized `typename` drops discriminated-union
          // narrowing, hence the structural read inside formatWithIdentifier.)
          case 'AgaveEquipment':
          case 'AgaveEmCostCode':
          case 'AgaveEmCostType': {
            return formatWithIdentifier(item, 'code', identifierOrder, 'name');
          }
        }
      }

      if ('source_id' in item) {
        const agaveItem = item as any;

        if ('number' in agaveItem && agaveItem.number) {
          return getAgaveGlLabel(agaveItem);
        }

        if ('project_id' in agaveItem) {
          return getAgaveCostCodeLabel(agaveItem);
        }

        return getAgaveVendorLabel(agaveItem);
      }

      const flexItem = item as any;

      if ('projectName' in flexItem && flexItem.projectName) {
        const projectCode = flexItem.projectCode;
        return flexItem.projectName + (projectCode ? ` (${projectCode})` : '');
      }

      if ('code' in flexItem && flexItem.code && !('source_id' in flexItem)) {
        return flexItem.code;
      }

      if ('name' in flexItem && flexItem.name) {
        return flexItem.name;
      }

      return '';
    },
    [
      getAgaveGlLabel,
      getAgaveProjectLabel,
      getAgaveCostCodeLabel,
      getAgaveCostTypeLabel,
      getAgaveVendorLabel,
      getAgaveSubJobLabel,
      getAgaveLocationLabel,
      getAgaveDepartmentLabel,
      getAgaveSubaccountLabel,
      getAgaveEmployeeLabel,
      // Read directly by the AgaveEmCostCode/AgaveEmCostType case. Listed
      // explicitly rather than relying on the getters above changing identity
      // when the setting flips — several were just pinned to the default, and
      // one more such cleanup would freeze the EM labels at mount-time format.
      identifierOrder,
    ]
  );

  return {
    getDisplayName,
    useAgave,
    useFlex,
    isQBDClient,
    isQBOClient,
  };
};
